import {
  DashboardOutlined,
  DownOutlined,
  GlobalOutlined,
  SoundOutlined,
  LogoutOutlined,
  SettingOutlined,
  UnorderedListOutlined,
  UserOutlined,
  TranslationOutlined,
  NotificationOutlined,
  SettingFilled,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from "@ant-design/icons";
import { App, Button, ConfigProvider, Dropdown, Layout, Menu, Space, theme } from "antd";
import React, { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { EndeavourProvider, useEndeavour } from "../hooks/useEndeavour";
import UpdatePassword from "../pages/UpdatePassword";
import { lightTheme } from "../theme";

const NAVY = "#0b1426";
const NAVY_LIGHT = "#0e1a30";
const GOLD = "#c8a55a";

const menuItems = [
  {
    key: "/",
    icon: <DashboardOutlined />,
    label: <Link to="/">Dashboard</Link>,
  },
  {
    key: "/users",
    icon: <UserOutlined />,
    label: <Link to="/users">Users</Link>,
  },
  {
    key: "/transactions",
    icon: <UnorderedListOutlined />,
    label: <Link to="/transactions">Transactions</Link>,
  },
  {
    key: "/notransaction",
    icon: <UnorderedListOutlined />,
    label: <Link to="/notransaction">No Transactions List</Link>,
  },
  {
    key: "/campaigns",
    icon: <SoundOutlined />,
    label: <Link to="/campaigns">Campaigns</Link>,
  },
  {
    key: "/campaign-rules",
    icon: <SettingFilled />,
    label: <Link to="/campaign-rules">Campaign Rules</Link>,
  },
  {
    key: "/translation",
    icon: <TranslationOutlined />,
    label: <Link to="/translation">i18n Translation</Link>,
  },
  {
    key: "/notification",
    icon: <NotificationOutlined />,
    label: <Link to="/notification">App Notification</Link>,
  },
];

const bestMatchingKey = (pathname, menuItems) => {
  let bestMatch = "";
  menuItems.forEach((item) => {
    if (pathname.startsWith(item.key) && item.key.length > bestMatch.length) {
      bestMatch = item.key;
    }
  });
  return bestMatch || "/";
};

function Container() {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const updatePWRef = React.useRef(null);

  const currentPath = location.pathname;
  const defaultSelectedKey = bestMatchingKey(currentPath, menuItems);

  const userMenuItems = [
    {
      key: "1",
      label: (
        <Space>
          <GlobalOutlined />
          Update Password
        </Space>
      ),
      onClick: () => {
        updatePWRef.current?.show();
      },
    },
    {
      key: "2",
      label: (
        <Space>
          <LogoutOutlined />
          Logout
        </Space>
      ),
      onClick: () => {
        localStorage.removeItem("access_token");
        window.location.replace("/");
      },
    },
  ];

  return (
    <ConfigProvider theme={lightTheme}>
      <App>
        <EndeavourProvider>
          <Layout style={{ minHeight: "100vh" }}>
            {/* Sidebar */}
            <Layout.Sider
              trigger={null}
              collapsible
              collapsed={collapsed}
              width={240}
              collapsedWidth={80}
              style={{
                background: `linear-gradient(180deg, ${NAVY_LIGHT}, ${NAVY})`,
                borderRight: "none",
                overflow: "auto",
                height: "100vh",
                position: "fixed",
                left: 0,
                top: 0,
                bottom: 0,
                zIndex: 100,
              }}
              theme="dark"
            >
              {/* Brand area */}
              <div
                style={{
                  height: 56,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: collapsed ? "center" : "flex-start",
                  padding: collapsed ? "0" : "0 20px",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
                }}
              >
                <span
                  style={{
                    fontFamily: "'Syne', sans-serif",
                    fontWeight: 800,
                    fontSize: 17,
                    color: GOLD,
                    letterSpacing: "0.3px",
                  }}
                >
                  {collapsed ? "PA" : "PA Alliance"}
                </span>
              </div>

              {/* Navigation */}
              <Menu
                theme="dark"
                mode="inline"
                selectedKeys={[defaultSelectedKey]}
                items={menuItems}
                style={{
                  background: "transparent",
                  borderRight: "none",
                  marginTop: 8,
                }}
              />

              {/* Collapse toggle */}
              <div
                onClick={() => setCollapsed(!collapsed)}
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 48,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  borderTop: "1px solid rgba(255, 255, 255, 0.06)",
                  color: "rgba(255, 255, 255, 0.55)",
                  transition: "color 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255, 255, 255, 0.85)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255, 255, 255, 0.55)")}
              >
                {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              </div>
            </Layout.Sider>

            {/* Main content area */}
            <Layout style={{ marginLeft: collapsed ? 80 : 240, transition: "margin-left 0.2s" }}>
              {/* Header */}
              <Layout.Header
                style={{
                  background: "#ffffff",
                  height: 56,
                  lineHeight: "56px",
                  padding: "0 32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  borderBottom: "1px solid #e5e7eb",
                  position: "sticky",
                  top: 0,
                  zIndex: 99,
                }}
              >
                <Dropdown menu={{ items: userMenuItems }} trigger={["click"]}>
                  <Button
                    type="text"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      color: "#374151",
                    }}
                  >
                    <SettingOutlined style={{ fontSize: 16 }} />
                    <DownOutlined style={{ fontSize: 10 }} />
                  </Button>
                </Dropdown>
              </Layout.Header>

              {/* Content */}
              <Layout.Content
                style={{
                  padding: "28px 32px",
                  background: "#f4f5f7",
                  minHeight: "calc(100vh - 56px - 48px)",
                }}
              >
                <Outlet />
              </Layout.Content>

              {/* Footer */}
              <Layout.Footer
                style={{
                  background: "#ffffff",
                  textAlign: "center",
                  padding: "16px 32px",
                  color: "#8c919a",
                  fontSize: 12,
                  borderTop: "1px solid #f0f0f0",
                }}
              >
                Powered by Payment Asia
              </Layout.Footer>
            </Layout>

            <UpdatePassword ref={updatePWRef} />
          </Layout>
        </EndeavourProvider>
      </App>
    </ConfigProvider>
  );
}

export default Container;
